<script setup>
// 登入彈窗，版面對齊設計稿（稿寬 1440、彈窗 508）。
// 下面的尺寸與間距都是量稿得來的值，要調整前先回頭對稿。
// 由 SiteHeader 帶出，開關狀態走 useLoginDialog()。
import logoMark from '~/assets/img/login-logo.png'
import googleIcon from '~/assets/img/icon-google.png'
import lineIcon from '~/assets/img/icon-line.png'
import h2uIcon from '~/assets/img/icon-h2u.png'

const route = useRoute()
const { fetchProviders, startSocialLogin } = useAuth()
const { isLoginOpen, loginRedirect, closeLogin } = useLoginDialog()
// 首頁自己有捲動容器，鎖 html 對它無害
const { lockScroll } = useScrollLock()

// 按鈕文案與圖示由前台決定，後端只回 platform 與 label
// 順序依設計稿：健行筆記在最上面
const PROVIDERS = [
  { platform: 'h2u', text: '使用健行筆記快速登入', icon: h2uIcon },
  { platform: 'google', text: '使用Google快速登入', icon: googleIcon },
  { platform: 'line', text: '使用LINE快速登入', icon: lineIcon }
]

// null＝還沒向後端問到平台清單（含問不到的情況），此時照設計稿把三顆都畫出來，
// 點下去才會拿到錯誤；問得到就以後端已完成設定的平台為準，沒設定的不顯示
const platforms = ref(null)
const errMsg = ref('')
// 各平台各自的等待狀態，避免點了 LINE 卻是 Google 的按鈕在轉
const pending = ref('')

const buttons = computed(() =>
  platforms.value === null
    ? PROVIDERS
    : PROVIDERS.filter((item) => platforms.value.includes(item.platform))
)

async function loadProviders() {
  try {
    const list = await fetchProviders()
    platforms.value = (list || []).map((item) => item.platform)
  } catch {
    platforms.value = null
  }
}

async function onLogin(platform) {
  if (pending.value) return
  pending.value = platform
  errMsg.value = ''
  try {
    // 成功時整頁跳去第三方，不會回到這裡
    await startSocialLogin(platform, safeRedirect(loginRedirect.value || route.fullPath, '/member'))
  } catch (err) {
    errMsg.value = err.message
    pending.value = ''
  }
}

function onKeydown(e) {
  if (e.key === 'Escape') closeLogin()
}

watch(isLoginOpen, (open) => {
  if (open) {
    errMsg.value = ''
    pending.value = ''
    if (platforms.value === null) loadProviders()
    window.addEventListener('keydown', onKeydown)
    lockScroll(true)
    return
  }
  window.removeEventListener('keydown', onKeydown)
  lockScroll(false)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <Transition name="lg">
        <div v-if="isLoginOpen" class="lg-mask" @click.self="closeLogin()">
          <div class="lg-panel" role="dialog" aria-modal="true" aria-labelledby="lg-title">
            <button class="lg-close" type="button" aria-label="關閉" @click="closeLogin()">
              <svg viewBox="0 0 14 14" width="14" height="14" aria-hidden="true">
                <path
                  d="M1 1l12 12M13 1L1 13"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
              </svg>
            </button>

            <img class="lg-logo" :src="logoMark" width="70" height="70" alt="" aria-hidden="true" />

            <h2 id="lg-title" class="lg-title">登入開始投票</h2>
            <p class="lg-sub">持有 Google 或 LINE 帳號即可參與 <br /> 115年全民親山步道票選活動</p>

            <p v-if="errMsg" class="lg-err">{{ errMsg }}</p>

            <div v-if="buttons.length" class="lg-btns">
              <button
                v-for="item in buttons"
                :key="item.platform"
                class="lg-btn"
                :class="`lg-btn-${item.platform}`"
                type="button"
                :disabled="!!pending"
                @click="onLogin(item.platform)"
              >
                <img class="lg-btn-ico" :src="item.icon" width="24" height="24" alt="" />
                <span>{{ pending === item.platform ? '前往登入頁…' : item.text }}</span>
              </button>
            </div>
            <p v-else class="lg-err">目前沒有可用的登入方式，請聯絡主辦單位</p>

            <p class="lg-note">
              登入即代表您同意本活動之隱私權政策與活動辦法。我們僅使用您的帳號資訊進行投票驗證,不會用於其他用途。
            </p>
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<style scoped>
.lg-mask {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  /* 稿上白色頁首被壓成 #7a7c7e，反推遮罩就是五成黑 */
  background: rgba(0, 0, 0, 0.5);
}

.lg-panel {
  position: relative;
  width: 100%;
  max-width: 508px;
  padding: 48px 46px 49px;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(var(--site-shadow-rgb), 0.28);
  font-family: var(--site-font);
  text-align: center;
}

.lg-close {
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--site-text);
  cursor: pointer;
  transition: color 0.25s;
}

.lg-close:hover {
  color: var(--site-orange);
}

/* 圖檔本身四周留了透明邊，直接用原尺寸擺才會落在稿上的位置 */
.lg-logo {
  display: block;
  width: 70px;
  height: 70px;
  margin: 0 auto;
  object-fit: contain;
}

.lg-title {
  margin: 12px 0 0;
  font-size: 32px;
  font-weight: 500;
  line-height: 1.4;
  color: #111;
}

.lg-sub {
  margin: 4px 0 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.6;
  color: var(--site-text);
}

.lg-err {
  margin: 16px 0 0;
  font-size: 14px;
  line-height: 1.6;
  color: #d93025;
}

.lg-btns {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 30px;
}

.lg-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
  height: 62px;
  padding: 0 16px;
  border: 0;
  border-radius: 999px;
  font-family: inherit;
  font-size: 20px;
  line-height: 1.2;
  cursor: pointer;
  transition:
    background-color 0.25s,
    color 0.25s,
    box-shadow 0.25s,
    transform 0.25s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.lg-btn:hover {
  transform: translateY(-2px);
}

.lg-btn:disabled {
  cursor: not-allowed;
  opacity: 0.65;
  transform: none;
}

.lg-btn-ico {
  display: block;
  flex: none;
  width: 24px;
  height: 24px;
  object-fit: contain;
}

/* 健行筆記與 Google 在稿上是同一種白底描邊鈕，差別只有圖示與文案 */
.lg-btn-google,
.lg-btn-h2u {
  border: 1px solid #333;
  background: #fff;
  color: #333;
  font-weight: 500;
}

.lg-btn-google:hover,
.lg-btn-h2u:hover {
  background: #f5f5f5;
  box-shadow: 0 10px 22px rgba(var(--site-shadow-rgb), 0.14);
}

/* 綠色取自設計稿（#00c300），不是 LINE 官方色票，改前先對稿 */
.lg-btn-line {
  background: #00c300;
  color: #fff;
  font-weight: 500;
}

.lg-btn-line:hover {
  background: #00ae00;
  box-shadow: 0 10px 22px rgba(0, 160, 0, 0.3);
}

.lg-note {
  /* 稿上這段剛好斷成兩行，靠這個寬度維持同樣的斷行 */
  max-width: 352px;
  margin: 28px auto 0;
  font-size: 14px;
  line-height: 1.5;
  color: #666;
}

.lg-enter-active,
.lg-leave-active {
  transition: opacity 0.25s ease;
}

.lg-enter-active .lg-panel,
.lg-leave-active .lg-panel {
  transition: transform 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.lg-enter-from,
.lg-leave-to {
  opacity: 0;
}

.lg-enter-from .lg-panel,
.lg-leave-to .lg-panel {
  transform: translateY(16px) scale(0.96);
}

@media (max-width: 560px) {
  .lg-mask {
    padding: 16px;
  }

  .lg-panel {
    padding: 36px 22px 32px;
    border-radius: 18px;
  }

  .lg-logo {
    width: 56px;
    height: 56px;
  }

  .lg-title {
    font-size: 24px;
  }

  .lg-sub {
    font-size: 14px;
  }

  .lg-btns {
    gap: 16px;
    margin-top: 24px;
  }

  .lg-btn {
    height: 52px;
    gap: 10px;
    font-size: 16px;
  }

  .lg-note {
    margin-top: 20px;
    font-size: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .lg-enter-active,
  .lg-leave-active,
  .lg-enter-active .lg-panel,
  .lg-leave-active .lg-panel,
  .lg-btn {
    transition: none;
  }

  .lg-btn:hover {
    transform: none;
  }
}
</style>
